import {
  LayoutDashboard,
  Activity,
  Route,
  Settings,
  LogOut,
  UserCircle,
  FileText,
  Smartphone,
  Users,
  ScrollText,
  HelpCircle,
  ChevronDown,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import logoMindsup from "@/assets/logo-mindsup.svg";
import { getEnvColor } from "@/lib/dynamicFavicon";
import { NavLink } from "@/components/NavLink";
import { GuardedLink } from "@/components/GuardedLink";
import { useAuth } from "@/hooks/useAuth";
import { useAdminProfile } from "@/hooks/useAdminProfile";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";

const navItems = [
  { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard },
  { title: "Activités", url: "/activities", icon: Activity },
  { title: "Parcours", url: "/paths", icon: Route },
  { title: "Utilisateurs", url: "/users", icon: Users },
  { title: "Paramètres", url: "/settings", icon: Settings },
];

const appVersion = (import.meta.env.VITE_APP_VERSION ?? "dev").trim();

const legalDocLinks = [
  { label: "CGU v1", to: "/legal/cgu" },
  { label: "Confidentialité v1", to: "/legal/privacy" },
  { label: "CGU v2", to: "/legal/cgu-v2" },
  { label: "Confidentialité v2", to: "/legal/privacy-v2" },
] as const;

const footerLinkClassName =
  "flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground";

export function AdminSidebar() {
  const { signOut, user, role } = useAuth();
  const { displayName } = useAdminProfile();
  const { enabled: standaloneActivities } = useFeatureFlag("feature_standalone_activities_enabled");
  const envColor = useMemo(() => getEnvColor(), []);
  const [legalOpen, setLegalOpen] = useState(false);

  return (
    <Sidebar>
      <SidebarHeader className="p-4">
        <div className="flex items-center gap-3">
          <img src={logoMindsup} alt="MindsUp" className="h-8" />
        </div>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {navItems.map((item) => {
                if (item.url === "/activities" && !standaloneActivities) return null;
                if (item.url === "/settings" && role !== "super_admin" && role !== "admin") return null;
                if (item.url === "/users" && role !== "super_admin" && role !== "admin") return null;
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild>
                      <NavLink
                        to={item.url}
                        className="hover:bg-sidebar-accent/50"
                        activeClassName="bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                      >
                        <item.icon className="mr-2 h-4 w-4" />
                        <span>{item.title}</span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="p-4">
        <div className="flex flex-col gap-2">
          <GuardedLink to="/api/docs" className={footerLinkClassName}>
            <FileText className="h-4 w-4" />
            API Doc
          </GuardedLink>
          <GuardedLink to="/simulator" className={footerLinkClassName}>
            <Smartphone className="h-4 w-4" />
            Simulateur
          </GuardedLink>
          <Collapsible open={legalOpen} onOpenChange={setLegalOpen}>
            <CollapsibleTrigger className={`${footerLinkClassName} w-full`}>
              <ScrollText className="h-4 w-4" />
              <span className="flex-1 text-left">Documents légaux</span>
              <ChevronDown
                className={`h-4 w-4 shrink-0 transition-transform ${legalOpen ? "rotate-180" : ""}`}
              />
            </CollapsibleTrigger>
            <CollapsibleContent className="ml-6 mt-1 flex flex-col gap-1 border-l pl-2">
              {legalDocLinks.map((item) => (
                <GuardedLink key={item.to} to={item.to} className={footerLinkClassName}>
                  {item.label}
                </GuardedLink>
              ))}
            </CollapsibleContent>
          </Collapsible>
          <GuardedLink to="/help" className={footerLinkClassName}>
            <HelpCircle className="h-4 w-4" />
            Aide
          </GuardedLink>
          <div className="flex items-center gap-2">
            <div className="flex-1 truncate text-sm font-medium">
              {displayName || user?.email}
            </div>
            <GuardedLink
              to="/profile"
              className="flex-shrink-0 rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
              title="Mon profil"
            >
              <UserCircle className="h-4 w-4" />
            </GuardedLink>
          </div>
          <button
            onClick={signOut}
            className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
          >
            <LogOut className="h-4 w-4" />
            Déconnexion
          </button>
          {appVersion ? (
            <p
              className="mt-2 break-all px-1 text-center font-mono text-[10px] leading-tight"
              title={appVersion}
              style={envColor ? { color: envColor } : undefined}
            >
              {appVersion}
            </p>
          ) : null}
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
